<template>
  <container v-if="data" ref="treemapContainer">
    <ParentSize @resize="setSize">
      <tooltip-provider
        :eventBus="tooltipEventBus"
        :chartMetadata="{ axis: chartData.axis, series: chartData.series }"
        :tooltipContentComponent="tileTooltipContentComponent"
        :tooltipContainerComponent="tileTooltipContainerComponent"
      >
        <template v-if="isTotal === false && categoryField">
          <TreemapNode
            v-for="d in treemapData.leaves()"
            :key="d.id"
            :style="{
              left: d.x0 + 'px',
              top: d.y0 + 'px',
              width: d.x1 - d.x0 + 'px',
              height: d.y1 - d.y0 + 'px',
              backgroundColor: color(d.data[regionField]),
            }"
            @mouseenter="handleMouseMove($event, d)"
          >
            <!-- <TreemapParentLabel>{{ d.data[regionField] }}</TreemapParentLabel> -->
          </TreemapNode>
        </template>
        <template v-else>
          <TreemapNode
            v-for="d in treemapData.children"
            :key="d.id"
            :style="{
              left: d.x0 + 'px',
              top: d.y0 + 'px',
              width: d.x1 - d.x0 + 'px',
              height: d.y1 - d.y0 + 'px',
              backgroundColor: categoryField ? color() : color(d.data[regionField]),
            }"
            @mouseenter="handleMouseMove($event, d)"
          >
            <!-- <TreemapParentLabel>{{ d.data[regionField] }}</TreemapParentLabel> -->
          </TreemapNode>
        </template>

        <TreemapParent
          v-for="d in treemapData.children"
          :key="'parent-category--' + d.id"
          :style="{
            width: d.x1 - d.x0 - 4 + 'px',
            height: d.y1 - d.y0 - 4 + 'px',
            transform: 'translate(' + (d.x0 + 2) + 'px,' + (d.y0 + 2) + 'px)',
          }"
        >
          <TreemapParentLabel :style="{ fontSize: fontSize(d), display: showLabel(d) }">
            {{ categoryField ? d.data[categoryField] : d.data[regionField] }}
          </TreemapParentLabel>
        </TreemapParent>
      </tooltip-provider>
    </ParentSize>
  </container>
</template>

<script>
import { treemap, stratify } from 'd3-hierarchy';

import { Container } from '../charts.styles';
import { ParentSize, ParentSizeMixin } from '../../responsive';
import { TreemapParent, TreemapParentLabel, TreemapNode } from './treeMapChart.styles';
import { ChartsMixin } from '../charts.mixin';
import { DEFAULT_FONT_SIZE } from '../../../constants';
import { TooltipProviderMixin } from '../tooltip';
import TileTooltipContent from './tileTooltip/tileTooltipContent.component';

export default {
  mixins: [ParentSizeMixin, ChartsMixin, TooltipProviderMixin],
  components: { Container, ParentSize, TreemapParent, TreemapParentLabel, TreemapNode },
  props: {
    data: Array,
    valueField: String,
    regionField: String,
    categoryField: { type: String, default: null },
    isTotal: { type: Boolean, default: false },
    color: { type: Function },
    tileTooltipContainerComponent: { type: Object },
    tileTooltipContentComponent: { type: Object, default: () => TileTooltipContent },
  },
  computed: {
    treemapData() {
      const stratifyFn = stratify()
        .id(d => d.id)
        .parentId(d => d.parent);

      let root = stratifyFn(this.data)
        .sum(d => d[this.valueField])
        .sort((a, b) => {
          return b.height - a.height || b.value - a.value;
        });

      const treemapFn = treemap()
        .size([this.parentWidth, this.parentHeight])
        .padding(this.isTotal ? 8 : 0)
        .paddingOuter(4)
        .round(true);

      treemapFn(root);

      // console.log('root', root);

      return root;
    },
  },
  methods: {
    handleMouseMove(event, d) {
      // console.log('d', d);

      const x = d.x0;
      const y = d.y0;
      const width = d.x1 - d.x0;
      const height = d.y1 - d.y0;

      const data = {
        id: d.id,
        data: {
          label: d.data[this.regionField] || d.data[this.categoryField],
          value: d.value,
        },
        name: d.id,
        targetBox: {
          x: x,
          y: y,
          width: width,
          height: height,
          centerHorizontally: true,
        },
      };

      this.showTooltip(d.id, data);
    },
    fontSize(d) {
      const innerHeight = d.y1 - d.y0 - 20;
      const innerWidth = d.x1 - d.x0 - 20;

      // set font size according to width
      let fontSize = (this.notZero(innerWidth) / 150) * DEFAULT_FONT_SIZE;

      // set font size according to height
      const lines = Math.floor(this.notZero(innerHeight) / fontSize);
      fontSize = Math.min(fontSize, this.notZero(innerHeight) / this.notZero(lines));

      return fontSize < 7 ? '7px' : fontSize + 'px';
    },
    showLabel(d) {
      const width = d.x1 - d.x0;
      const height = d.y1 - d.y0;

      if (width * height < 1000) {
        return 'none';
      }

      return 'block';
    },
    notZero(n) {
      // Coerce to number
      n = +n;
      // Matches +0, -0, NaN
      if (!n) {
        // throw new Error('Invalid dividend ' + n);
        return 0.00000001;
      }
      return n;
    },
  },
};
</script>
